// "From the farm — N waiting": the farm loads sent to this berry packhouse that
// nobody has received yet. Tapping Receive opens the normal berry intake form,
// pre-filled from the load. The save is receive_berry_farm_load (mig
// 20261140000000), which writes the delivery and closes the load together.
//
// Shown on DATA PRESENCE, like the citrus inbox: no pending load, no section.
// A FAILED read says so. It never renders as "nothing waiting".
import { Inbox } from 'lucide-react'
import MutationOverlays, { errorHandledElsewhere } from '@/components/MutationOverlays'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { withTimeout } from '@/lib/saveTimeout'
import { promptDialog, alertDialog } from '@/components/Dialogs'
import { friendlyError, throwFriendly } from '@/lib/friendlyError'
import { useCurrentUser } from '@/lib/useCurrentUser'
import { usePlatformView, useSupportView } from '@/lib/platformView'
import { canAnswerFarmLoad } from '@/lib/roles'
import { tenantRoleOf } from '@/modules/farm/blockEditGate'
import { FARM_LOAD_DOMAIN } from '@/modules/shared/PendingFarmLoads'
import { usePendingFarmLoads, type PendingFarmLoad } from '@/hooks/usePendingFarmLoads'
import { useGrowers } from '@/hooks/useGrowerData'
import { fmtDateZA } from '@/lib/dates'
import { unitNounFor } from '@/lib/unitNoun'

function plural(n: number, label: string): string {
  const noun = unitNounFor([label])
  return `${n} ${n === 1 ? noun.singular : noun.plural}`
}

/** The button, gated by reject_farm_load's own rule: MANAGER / ADMIN / SUPER_ADMIN of
 *  the active tenant, or platform mode; a support view is read-only; an unknown
 *  profile shows nothing. Its role hooks live here so they run only when a load waits. */
function NotComingButton({ disabled, onTap }: { disabled: boolean; onTap: () => void }) {
  const me = useCurrentUser().data
  const platformMode = usePlatformView()
  const supportView  = useSupportView()
  const can = me !== undefined && !supportView && (platformMode || canAnswerFarmLoad(tenantRoleOf(me)))
  if (!can) return null
  return (
    <button type="button" disabled={disabled} onClick={onTap}
      className="min-h-[44px] px-3 rounded-lg surface hover:bg-raised text-fg-secondary text-sm disabled:opacity-50">
      Not coming
    </button>
  )
}

export default function BerryFarmLoadsInbox({ tenantId, onReceive }: {
  tenantId: string | null
  onReceive: (l: PendingFarmLoad) => void
}) {
  const loadsQ = usePendingFarmLoads(tenantId)
  const { data: growers = [] } = useGrowers()
  const qc = useQueryClient()
  // The question is asked BEFORE mutate() (CLAUDE.md save-watchdog rule).
  const notComing = useMutation({
    mutationFn: async (a: { loadId: string; reason: string }) => {
      const { error } = await withTimeout(
        supabase.rpc('reject_farm_load', { p_load_id: a.loadId, p_reason: a.reason }),
        'BerryFarmLoadsInbox: reject_farm_load')
      if (error) throwFriendly(error, FARM_LOAD_DOMAIN)
    },
    onSettled: () => { void qc.invalidateQueries({ queryKey: ['pending-farm-loads'] }) },
    onError: async (e: Error) => {
      await alertDialog({ title: 'Could not do that', message: friendlyError(e, FARM_LOAD_DOMAIN).message })
    },
  })
  async function startNotComing(l: PendingFarmLoad) {
    const reason = await promptDialog({
      title: 'This load is not coming?',
      message: 'The farm sees this reason on their Loads register. Say what happened so they can act on it.',
      confirm: 'Not coming',
    })
    if (!reason || !reason.trim()) return
    notComing.mutate({ loadId: l.load_id, reason: reason.trim() })
  }

  if (loadsQ.isError) {
    return (
      <p className="card p-3 text-xs text-red-300 print:hidden">
        Couldn&apos;t load the farm inbox. Loads may be waiting. Retry before you assume none.
      </p>
    )
  }
  const loads = loadsQ.data ?? []
  const overlay = <MutationOverlays of={[[errorHandledElsewhere(notComing), 'Load marked not coming']]} />
  // Rendered in the empty case too: the last load vanishing must not eat the tick.
  if (loads.length === 0) return overlay

  return (
    <section className="space-y-2 print:hidden" aria-label="From the farm">
      {overlay}
      <h2 className="text-sm font-semibold text-fg-secondary flex items-center gap-2">
        <Inbox size={16} /> From the farm — {loads.length} waiting
      </h2>
      {loads.map(l => {
        const grower = growers.find(g => g.grower_id === l.grower_id)?.name
        return (
          <div key={l.load_id} className="card p-3 space-y-2">
            <div className="min-w-0">
              <p className="text-sm font-semibold">
                {plural(l.container_count, l.container_label)}
                {grower && <span className="text-fg-muted"> · {grower}</span>}
              </p>
              <p className="text-caption text-fg-muted">
                Sent {fmtDateZA(l.submitted_at.slice(0, 10))}
                {l.variety && ` · farm says: ${l.variety}`}
                {l.vehicle_ref && ` · ${l.vehicle_ref}`}
                {l.crate_count != null && ` · ${l.crate_count} crates`}
                {l.estimated_kg != null && ` · ~${l.estimated_kg} kg`}
              </p>
            </div>
            {l.notes_for_packhouse && (
              <p className="text-caption text-amber-200 bg-amber-900/20 rounded px-2 py-1">{l.notes_for_packhouse}</p>
            )}
            <div className="flex gap-2">
              <button type="button" onClick={() => onReceive(l)}
                className="flex-1 min-h-[44px] rounded-lg bg-emerald-700 hover:bg-emerald-600 text-white text-sm font-semibold">
                Receive
              </button>
              <NotComingButton disabled={notComing.isPending} onTap={() => void startNotComing(l)} />
            </div>
          </div>
        )
      })}
    </section>
  )
}
